import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { JobFilterParams } from 'src/app/pages/company/company-frontend/main-pages/jobs/services/job-list.service';
import { CompanyProfileService } from 'src/app/pages/company/company-frontend/main-pages/company-profile/services/company-profile.service';
import { environment } from 'src/environments/environment';

type SalaryTab = 'Hourly' | 'Monthly' | 'Yearly';

interface JobViewModel {
  id: string;
  title: string;
  jobPostTitle?: string;
  positionTitle?: string;
  company: string;
  companyId: string;
  type: string;
  location: string;
  workplace: string;
  salaryMin: number | null;
  salaryMax: number | null;
  hideSalary: boolean;
  postedAgo: string;
  postedDaysAgo: number;
  minExperience: number | null;
  maxExperience: number | null;
  department: string;
  jobLevel: string;
  logoUrl: string;
  companyInitial: string;
  jobDescription: string;
  currency: string;
  isNew: boolean;
}

function timeAgo(dateStr: string): { label: string; daysAgo: number } {
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return { label: `${Math.max(diffMins, 0)} min ago`, daysAgo: 0 };
  if (diffHours < 24) return { label: `${Math.max(diffHours, 0)}h ago`, daysAgo: 0 };
  if (diffDays === 1) return { label: '1 day ago', daysAgo: 1 };
  return { label: `${Math.max(diffDays, 0)} days ago`, daysAgo: diffDays };
}

function mapApiJob(apiJob: any): JobViewModel {
  const { label, daysAgo } = timeAgo(apiJob.createdAt);
  const companyName = apiJob.confidential 
    ? 'Confidential Company' 
    : (apiJob.companyName ?? 'Unknown Company');

  return {
    id: String(apiJob.id),
    title: apiJob.jobPostTitle ?? apiJob.position?.title ?? 'Unknown Position',
    jobPostTitle: apiJob.jobPostTitle,
    positionTitle: apiJob.positionTitle ?? apiJob.position?.title,
    company: companyName,
    companyId: apiJob.companyId ?? '',
    type: apiJob.jobType ?? 'Full-time',
    location:
      apiJob.city && apiJob.country
        ? `${apiJob.city}, ${apiJob.country}`
        : (apiJob.city ?? apiJob.country ?? 'N/A'),
    workplace: apiJob.workplace ?? '',
    salaryMin: apiJob.hideSalary ? null : (apiJob.minSalary ?? null),
    salaryMax: apiJob.hideSalary ? null : (apiJob.maxSalary ?? null),
    hideSalary: apiJob.hideSalary ?? false,
    postedAgo: label,
    postedDaysAgo: daysAgo,
    minExperience: apiJob.minExperience ?? null,
    maxExperience: apiJob.maxExperience ?? null,
    department: apiJob.position?.department ?? '',
    jobLevel: apiJob.jobLevel ?? apiJob.position?.jobLevel ?? '',
    logoUrl: apiJob.confidential
      ? ''
      : apiJob.companyLogoUrl
        ? `${environment.apiImagePath}${String(apiJob.companyLogoUrl).replace(/\\/g, '/')}`
        : '',
    companyInitial: apiJob.confidential
      ? 'C'
      : companyName.charAt(0).toUpperCase() || 'C',
    jobDescription: apiJob.jobDescription ?? 'No description provided.',
    currency: apiJob.currency ?? '$',
    isNew: daysAgo === 0,
  };
}

@Component({
  selector: 'app-joblist',
  templateUrl: './joblist.component.html',
  styleUrls: ['./joblist.component.css'],
})
export class JoblistComponent implements OnInit, OnChanges, OnDestroy {
  @Input() companyId: string = '';

  jobs: JobViewModel[] = [];
  isLoading = true;
  hasError = false;

  alertEmail = '';
  emailError = '';
  emailSuccess = '';

  activeSalaryTab: SalaryTab = 'Yearly';
  selectedSalaryRanges: string[] = [];
  selectedDatePosting = '';
  selectedExperience = '';
  selectedEmploymentTypes: string[] = [];
  selectedPostType = '';
  selectedWorkplace = '';
  filtersOpen = false;

  salaryOptions: Record<SalaryTab, string[]> = {
    Hourly: ['<$15/hr', '$15-$25/hr', '$25-$50/hr', '$50+/hr'],
    Monthly: ['<$2k/mo', '$2k-$4k/mo', '$4k-$7k/mo', '$7k+/mo'],
    Yearly: ['<$30K', '$30K-$50K', '$50K-$80K', '$80K+'],
  };

  dateOptions = [
    { label: 'All time', value: 'ALL_TIME' },
    { label: 'Last 24 hours', value: 'LAST_24_HOURS' },
    { label: 'Last 3 days', value: 'LAST_3_DAYS' },
    { label: 'Last 7 days', value: 'LAST_7_DAYS' },
  ];

  experienceOptions: {
    label: string;
    value: string;
    min: number | null;
    max: number | null;
  }[] = [
    { label: 'Any experience', value: '', min: null, max: null },
    { label: '0-1 year', value: '0-1', min: 0, max: 1 },
    { label: '1-3 years', value: '1-3', min: 1, max: 3 },
    { label: '3-5 years', value: '3-5', min: 3, max: 5 },
    { label: '5-10 years', value: '5-10', min: 5, max: 10 },
    { label: '10+ years', value: '10+', min: 10, max: null },
  ];

  employmentOptions = ['Full-time', 'Part-time'];

  postTypeOptions = [
    { label: 'All posts', value: '' },
    { label: 'Jobs', value: 'JOB' },
    { label: 'Internships', value: 'INTERNSHIP' },
  ];

  workplaceOptions = [
    { label: 'All', value: '' },
    { label: 'Remote', value: 'REMOTE_JOB' },
    { label: 'Near Me', value: 'NEAR_ME' },
    { label: 'Exact Location', value: 'EXACT_LOCATION' },
  ];

  private readonly salaryTypeMap: Record<SalaryTab, 'PER_HOUR' | 'PER_MONTH' | 'PER_YEAR'> = {
    Hourly: 'PER_HOUR',
    Monthly: 'PER_MONTH',
    Yearly: 'PER_YEAR',
  };

  private readonly salaryRangeValues: Record<string, { min: number | null; max: number | null }> = {
    '<$15/hr': { min: null, max: 15 },
    '$15-$25/hr': { min: 15, max: 25 },
    '$25-$50/hr': { min: 25, max: 50 },
    '$50+/hr': { min: 50, max: null },
    '<$2k/mo': { min: null, max: 2000 },
    '$2k-$4k/mo': { min: 2000, max: 4000 },
    '$4k-$7k/mo': { min: 4000, max: 7000 },
    '$7k+/mo': { min: 7000, max: null },
    '<$30K': { min: null, max: 30000 },
    '$30K-$50K': { min: 30000, max: 50000 },
    '$50K-$80K': { min: 50000, max: 80000 },
    '$80K+': { min: 80000, max: null },
  };

  constructor(
    private companyService: CompanyProfileService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.companyId) {
      this.loadJobs();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && this.companyId) {
      this.loadJobs();
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
  }

  get filteredJobs(): JobViewModel[] {
    return this.jobs;
  }

  get currentSalaryOptions(): string[] {
    return this.salaryOptions[this.activeSalaryTab];
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.selectedSalaryRanges.length) count++;
    if (this.selectedDatePosting) count++;
    if (this.selectedExperience) count++;
    if (this.selectedEmploymentTypes.length) count++;
    if (this.selectedPostType) count++;
    if (this.selectedWorkplace) count++;
    return count;
  }

  isSalarySelected(range: string): boolean {
    return this.selectedSalaryRanges.includes(range);
  }

  isEmploymentSelected(type: string): boolean {
    return this.selectedEmploymentTypes.includes(type);
  }

  navigateToJob(jobId: string): void {
    this.router.navigate(['/job-description', jobId]);
  }

  openFilters(): void {
    this.filtersOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeFilters(): void {
    this.filtersOpen = false;
    document.body.style.overflow = '';
  }

  formatSalary(val: number | null, currency: string): string {
    if (val === null) return '';
    return val >= 1000
      ? `${(val / 1000).toFixed(0)}K ${currency}`
      : `${val} ${currency}`;
  }

  getWorkplaceLabel(workplace: string): string {
    const map: Record<string, string> = {
      REMOTE: 'Remote',
      ON_SITE: 'On-site',
      HYBRID: 'Hybrid',
    };
    return map[workplace] ?? workplace;
  }

  private buildFilters(): JobFilterParams {
    const filters: JobFilterParams = {};

    if (this.selectedEmploymentTypes.length) {
      filters.jobTypes = [...this.selectedEmploymentTypes];
    }

    if (this.selectedPostType) {
      filters.postTypes = [this.selectedPostType as 'JOB' | 'INTERNSHIP'];
    }

    if (this.selectedDatePosting) {
      filters.postingDate = this.selectedDatePosting as JobFilterParams['postingDate'];
    }

    if (this.selectedWorkplace) {
      filters.location = this.selectedWorkplace as JobFilterParams['location'];
    }

    if (this.selectedExperience) {
      const opt = this.experienceOptions.find((item) => item.value === this.selectedExperience);
      if (opt) {
        if (opt.min !== null) {
          filters.minExperience = opt.min;
        }
        if (opt.max !== null) {
          filters.maxExperience = opt.max;
        }
      }
    }

    if (this.selectedSalaryRanges.length) {
      const salaryType = this.salaryTypeMap[this.activeSalaryTab];
      filters.salaryFilters = this.selectedSalaryRanges
        .map((range) => this.salaryRangeValues[range])
        .filter(Boolean)
        .map(({ min, max }) => ({
          minSalary: min,
          maxSalary: max,
          salaryType,
        }));
    }

    return filters;
  }

loadJobs(): void {
  this.isLoading = true;
  this.hasError = false;

  this.companyService.getCompanyJobs(this.companyId, this.buildFilters()).subscribe({
    next: (res: any) => {
      const list = Array.isArray(res)
        ? res
        : (res?.jobPosts?.content ?? res?.content ?? []);
      this.jobs = list.map((job: any) => mapApiJob(job));
      this.isLoading = false;
    },
    error: () => {
      this.hasError = true;
      this.isLoading = false;
    },
  });
}

  setSalaryTab(tab: SalaryTab): void {
    this.activeSalaryTab = tab;
    this.selectedSalaryRanges = [];
    this.loadJobs();
  }

  toggleSalaryRange(range: string): void {
    const idx = this.selectedSalaryRanges.indexOf(range);
    if (idx > -1) {
      this.selectedSalaryRanges.splice(idx, 1);
    } else {
      this.selectedSalaryRanges.push(range);
    }
    this.loadJobs();
  }

  toggleEmploymentType(type: string): void {
    const idx = this.selectedEmploymentTypes.indexOf(type);
    if (idx > -1) {
      this.selectedEmploymentTypes.splice(idx, 1);
    } else {
      this.selectedEmploymentTypes.push(type);
    }
    this.loadJobs();
  }

  clearFilters(): void {
    this.activeSalaryTab = 'Yearly';
    this.selectedSalaryRanges = [];
    this.selectedDatePosting = '';
    this.selectedExperience = '';
    this.selectedEmploymentTypes = [];
    this.selectedPostType = '';
    this.selectedWorkplace = '';
    this.loadJobs();
  }

  subscribeEmail(): void {
    this.emailError = '';
    this.emailSuccess = '';
    const email = this.alertEmail.trim();
    if (!email) {
      this.emailError = 'Please enter your email.';
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.emailError = 'Please enter a valid email address.';
      return;
    }
    this.emailSuccess = "✓ Subscribed! We'll notify you of new jobs.";
    this.alertEmail = '';
  }
}
